import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';
import { Sparkles, X, ChevronLeft, ChevronRight, Newspaper } from 'lucide-react';

const SESSION_KEY = 'chico_news_popup_seen';

const buildImageUrl = (apiUrl, coverUrl) => {
  if (!coverUrl) return null;
  return coverUrl.startsWith('http') ? coverUrl : `${apiUrl}${coverUrl}`;
};

const NewsPopup = () => {
  const { t } = useTranslation();
  const [posts, setPosts] = useState([]);
  const [open, setOpen] = useState(false);
  const [index, setIndex] = useState(0);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const fetchFeatured = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/news/featured`);
      if (!data || data.length === 0) return;

      // Only show once per browser session
      const seen = sessionStorage.getItem(SESSION_KEY);
      if (seen === '1') return;

      setPosts(data);
      // Delay open by 800ms for a smoother UX
      setTimeout(() => setOpen(true), 800);
    } catch (error) {
      console.error('Erro ao carregar destaques:', error);
    }
  }, [API_URL]);

  useEffect(() => {
    fetchFeatured();
  }, [fetchFeatured]);

  const handleClose = () => {
    sessionStorage.setItem(SESSION_KEY, '1');
    setOpen(false);
  };

  if (!open || posts.length === 0) return null;

  const post = posts[index];
  const img = buildImageUrl(API_URL, post.cover_url);
  const hasMultiple = posts.length > 1;

  const prev = () => setIndex((i) => (i - 1 + posts.length) % posts.length);
  const next = () => setIndex((i) => (i + 1) % posts.length);

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); }}>
      <DialogContent
        className="max-w-2xl p-0 overflow-hidden gap-0"
        data-testid="news-popup"
        aria-describedby="news-popup-summary"
      >
        <DialogTitle className="sr-only">{post.title}</DialogTitle>
        <DialogDescription id="news-popup-summary" className="sr-only">
          {post.summary}
        </DialogDescription>
        <div className="relative">
          {/* Hero banner with image or gradient */}
          <div className="relative h-56 md:h-72 overflow-hidden">
            {img ? (
              <img
                src={img}
                alt={post.title}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-teal-500 via-teal-600 to-blue-700 flex items-center justify-center">
                <Sparkles size={56} className="text-white/40" aria-hidden="true" />
              </div>
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />

            {/* Featured badge */}
            <div className="absolute top-4 left-4 inline-flex items-center gap-2 bg-amber-400 text-amber-950 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider shadow-lg">
              <Sparkles size={12} aria-hidden="true" />
              {t('news.popup.featured')}
            </div>

            {/* Close button */}
            <button
              type="button"
              onClick={handleClose}
              data-testid="news-popup-close"
              aria-label={t('news.popup.close')}
              className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/90 hover:bg-white text-gray-800 flex items-center justify-center shadow-lg transition-colors focus:outline-none focus:ring-2 focus:ring-teal-500"
            >
              <X size={18} aria-hidden="true" />
            </button>

            {/* Carousel arrows */}
            {hasMultiple && (
              <>
                <button
                  type="button"
                  onClick={prev}
                  aria-label={t('news.popup.previous')}
                  data-testid="news-popup-prev"
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/90 hover:bg-white text-gray-800 flex items-center justify-center shadow-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                >
                  <ChevronLeft size={20} aria-hidden="true" />
                </button>
                <button
                  type="button"
                  onClick={next}
                  aria-label={t('news.popup.next')}
                  data-testid="news-popup-next"
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/90 hover:bg-white text-gray-800 flex items-center justify-center shadow-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                >
                  <ChevronRight size={20} aria-hidden="true" />
                </button>
              </>
            )}

            {/* Category badge bottom-left */}
            <span
              className={`absolute bottom-4 left-4 inline-flex items-center gap-1 px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider ${
                post.category === 'project'
                  ? 'bg-amber-100 text-amber-800'
                  : 'bg-teal-100 text-teal-800'
              }`}
            >
              {post.category === 'project' ? (
                <Sparkles size={12} aria-hidden="true" />
              ) : (
                <Newspaper size={12} aria-hidden="true" />
              )}
              {post.category === 'project' ? t('news.badgeProject') : t('news.badgeNews')}
            </span>
          </div>

          {/* Content */}
          <div className="p-6 md:p-8 max-h-[60vh] overflow-y-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 leading-tight mb-3">
              {post.title}
            </h2>
            <p className="text-base text-gray-700 mb-5">{post.summary}</p>
            <div className="prose prose-sm max-w-none text-gray-800 prose-headings:text-gray-900 prose-a:text-teal-600 prose-strong:text-gray-900">
              <ReactMarkdown>{post.content || ''}</ReactMarkdown>
            </div>

            {/* Dots indicator */}
            {hasMultiple && (
              <div className="flex justify-center items-center gap-1.5 mt-6" aria-label={t('news.popup.indicator')}>
                {posts.map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setIndex(i)}
                    aria-label={`${t('news.popup.goto')} ${i + 1}`}
                    aria-current={i === index}
                    className={`h-2 rounded-full transition-all focus:outline-none focus:ring-2 focus:ring-teal-500 ${
                      i === index ? 'bg-teal-600 w-8' : 'bg-gray-300 hover:bg-gray-400 w-2'
                    }`}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default NewsPopup;
